import { useCallback, useEffect, useState } from 'react';
import { read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from '../../../../../utils/url-manager';

/**
 * A Nok offered to a run, with whatever it needs answering before it can score a reply.
 *
 * The shape comes from `NokComposerCatalogue` in `hq_api` and is the same one the customer
 * composer receives, which is the point: one catalogue, two composers, no drift.
 */
export interface AdminMiniNok {
    id: number;
    name: string;
    type: string;
    description: string;
    key: string;
    tag: string;
    tags: string[];
    configuration: AdminMiniNokConfiguration;
}

export interface AdminMiniNokConfiguration {
    system_data_point: string;
    processing_type: string;
    /** Which control to render: single, address, multiple, or none when it asks for nothing. */
    type: string;
    /** Where the value comes from: company, recipient, manual, or none. */
    source: string;
    required: boolean;
    /** Whether the company already answered it. False on an admin run with no company picked. */
    prefilled: boolean;
    complex: boolean;
    complex_value?: Record<string, unknown>;
    [key: string]: unknown;
}

/** One Nok switched on for a run, and the answers given for it. */
export interface ChosenNok extends AdminMiniNok {
    configuration: AdminMiniNokConfiguration;
}

/**
 * The Noks a Mini offers, loaded for the admin composer.
 *
 * **Reloads when the company changes**, because the company is what prefills four of the ten data
 * points. Picking a company after switching a Nok on has to refill its company name and address,
 * and unpicking one has to empty them again, or the run is saved carrying another company's
 * details.
 */
export function useAdminMiniNoks(systemCode: string, companyId: string): {
    noks: AdminMiniNok[],
    loading: boolean,
    error: string,
    reload: () => void,
} {
    const [noks, setNoks] = useState<AdminMiniNok[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const load = useCallback(async (): Promise<void> => {
        setLoading(true);
        setError('');

        const url: string = UseUrlManagerCleanUrl({ url: hostUrl + 'hq/mini/admin/noks' });
        const token: string = read_cookie('auth');

        try {
            const response: Response = await fetch(new Request(url, {
                method: 'POST',
                body: JSON.stringify({ systemCode: systemCode, companyId: companyId }),
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'token': token,
                    'hq': 'hq',
                }),
            }));

            // 403 means the role lacks `HQ_Nok::noks.view`, and the body is an access denied page
            // rather than JSON. Parsing it produces "unexpected token <", which tells the admin
            // nothing about what is wrong.
            if (response.status === 403) {
                setError('Your role does not include reading Noks, so none can be offered.');
                setNoks([]);

                return;
            }

            const body = await response.json();
            if (body?.status !== 'success') {
                setError(String(body?.message ?? 'The Noks could not be loaded.'));
                setNoks([]);

                return;
            }

            setNoks(Array.isArray(body.data) ? body.data : []);
        } catch (caught: unknown) {
            setError(caught instanceof Error ? caught.message : 'The Noks could not be loaded.');
            setNoks([]);
        } finally {
            setLoading(false);
        }
    }, [systemCode, companyId]);

    useEffect((): void => {
        void load();
    }, [load]);

    return { noks, loading, error, reload: (): void => { void load(); } };
}

/** Whether switching this Nok on should ask the admin for anything at all. */
export function nokNeedsAnswering(nok: AdminMiniNok): boolean {
    return nok.configuration?.type !== 'none' || nok.configuration?.complex === true;
}

/** The key a data point's value is stored under, e.g. `company_name_value`. */
export function valueKeyFor(nok: AdminMiniNok): string {
    return `${ nok.configuration?.system_data_point ?? '' }_value`;
}

/** Empty, for every shape a data point value can take. */
export function isAnswerEmpty(value: unknown): boolean {
    if (value === undefined || value === null) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        return value.filter((item: unknown): boolean => String(item ?? '').trim() !== '').length === 0;
    }
    if (typeof value === 'object') {
        return Object.values(value as Record<string, unknown>)
            .filter((item: unknown): boolean => String(item ?? '').trim() !== '').length === 0;
    }

    return false;
}

/**
 * The chosen Noks that are still missing a required answer.
 *
 * **Recipient-sourced points are never counted**, whatever they hold. They are answered per
 * contact at send time from the recipient's own record, and anything collected in the composer is
 * only a fallback - which is why HQ-API marks them `required: false` rather than leaving the
 * composer to know that.
 */
export function noksMissingAnswers(chosen: ChosenNok[]): ChosenNok[] {
    return chosen.filter((nok: ChosenNok): boolean => {
        const configuration: AdminMiniNokConfiguration = nok.configuration;
        if (configuration?.required !== true || configuration?.source === 'recipient') {
            return false;
        }

        return isAnswerEmpty(configuration[valueKeyFor(nok)]);
    });
}
